import React, { useState, useEffect, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import styled from "styled-components";
import _ from "lodash";
import sort from "fast-sort";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import { State } from "src/state/ducks";
import * as OrdersActions from "src/state/ducks/order/actions";
import * as NotificationActions from "src/state/ducks/notification/actions";
import { OrderEntity } from "src/firebase/db/Orders";
import { UserEntity } from "src/firebase/db/Users";

import { formatDate, formatDateToDate } from "src/helpers/UtilityHelper";
import StyleConfig from "src/constants/StyleConfig";
import { DefaultPageProps, HistoryProps } from "src/constants/Interface";

import { DefaultLayout } from "src/components/layouts/DefaultLayout";
import Heading from "src/components/atoms/typo/Heading";
import PlainHomeButton from "src/components/atoms/button/PlainHomeButton";
import SpinnerModal from "src/components/organisms/loading/SpinnerModal";
import ReservationOrderDetailList from "src/components/organisms/list/ReservationOrderDetailList";
import ReservationOrderDetailFilterForm from "src/components/organisms/form/ReservationOrderDetailFilterForm";

dayjs.extend(isSameOrAfter);

type OwnProps = {
  id: string;
  date: string;
};

type Props = DefaultPageProps &
  HistoryProps &
  OwnProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const ReservationOrderDetailPage = (props: Props) => {
  const [query, setQuery] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [allSuggestions, setAllSuggestions] = useState<string[]>([]);
  const [filterByStatus, setFilterByStatus] = useState("準備する商品を表示");

  const {
    id,
    date,
    isFetching,
    orders,
    users,
    usersOrders,
    orderBread,
    notification,
    getDayReservation,
    updateIsConfirmed,
    updateIsCompleted,
  } = props;

  const fetch = useCallback(() => {
    getDayReservation(id);
  }, [getDayReservation, id]);

  // 受取日をDateオブジェクトとして返す
  const lastPickupDays = useMemo(() => {
    if (!notification) {
      return [];
    }
    const days = notification.last_pickup_days as (Date | undefined)[];
    return days.filter((v) => v).map((v) => formatDateToDate(v)) as Date[];
  }, [notification]);

  // パンの名称
  const breadName = useMemo(() => (orderBread ? orderBread.name : "-"), [
    orderBread,
  ]);

  // 総販売数
  const totalSoldQuntity = useMemo(() => {
    const quantities = orders
      .filter(({ status }) => status === "succeeded")
      .map(({ items }) => items.map(({ quantity }) => quantity));
    return _(quantities).flatten().sum();
  }, [orders]);

  // 在庫
  const stock = useMemo(() => (orderBread ? orderBread.stock : "-"), [
    orderBread,
  ]);

  // 初期表示：URIから取得した日付内の通知を取得
  useEffect(() => {
    fetch();
  }, [fetch]);

  // 初期表示：オートコンプリート用の配列取得
  useEffect(() => {
    const orderNumbers = orders.map(({ order_number }) => order_number);
    const userNames = users.map(({ name }) => name);
    const values = _.concat(orderNumbers, userNames);
    const sorted = sort(_.uniq(values)).asc();
    setSuggestions(sorted);
    setAllSuggestions(sorted);
  }, [orders, users, setSuggestions, setAllSuggestions]);

  // 初期表示：検索用の日付
  useEffect(() => {
    // クエリが存在する場合
    if (date) {
      setCurrentDate(dayjs(date).toDate());
      return;
    }

    const d = lastPickupDays.length ? lastPickupDays[0] : new Date();
    setCurrentDate(d);
  }, [date, lastPickupDays]);

  // 対象注文以前の注文を取得
  const filterPrevOrders = useCallback(
    (order: OrderEntity) => {
      const { user_id, created_at } = order;
      const date = dayjs(formatDateToDate(created_at));
      return _(usersOrders)
        .filter(
          (order) =>
            order.user_id === user_id &&
            order.shop_id === notification.shop_id &&
            order.status === "succeeded" &&
            date.isSameOrAfter(formatDateToDate(order.created_at))
        )
        .value() as OrderEntity[];
    },
    [notification.shop_id, usersOrders]
  );

  // 注文に紐づくユーザーを取得
  const filterUsers = useCallback(
    (order: OrderEntity) => {
      const { user_id } = order;
      return users.find((user) => user.user_id === user_id) as UserEntity;
    },
    [users]
  );

  // クエリを配列に変換
  const queries = useMemo(() => {
    const str = query.replace(/　/g, " ");
    return _(str.split(" "))
      .uniq()
      .remove((v) => v)
      .value();
  }, [query]);

  // 注文に紐づくユーザーを取得
  const isMathedQuery = useCallback(
    (target: string) => {
      return queries.some((q) => target.indexOf(q.toLowerCase()) !== -1);
    },
    [queries]
  );

  // 検索条件で絞り込んだ注文データ
  const filteredOrders = useMemo(() => {
    if (!queries.length || queries[0] === "") {
      return orders;
    }
    return orders.filter((order) => {
      const user = filterUsers(order);
      const userName = user?.name || "";
      const orderNumber = order?.order_number || "";
      const isMathedOrderNumber = isMathedQuery(orderNumber.toLowerCase());
      const isMathedUserName = isMathedQuery(userName.toLowerCase());
      return queries.length > 1
        ? isMathedOrderNumber && isMathedUserName
        : isMathedOrderNumber || isMathedUserName;
    });
  }, [orders, queries, filterUsers, isMathedQuery]);

  const onUpdateIsConfirmed = useCallback(
    (orderId: string, isConfirmed: boolean) => {
      updateIsConfirmed(orderId, isConfirmed);
      getDayReservation(id);
    },
    [updateIsConfirmed, getDayReservation, id]
  );

  const onUpdateIsCompleted = useCallback(
    (orderId: string, isCompleted: boolean) => {
      updateIsCompleted(orderId, isCompleted);
      getDayReservation(id);
    },
    [updateIsCompleted, getDayReservation, id]
  );

  // 検索クエリの変更
  const onChangeQuery = useCallback(
    (v: string) => {
      setQuery(v);
    },
    [setQuery]
  );

  // 検索クエリが選択された時
  const onSuggestionSelected = useCallback(
    (v: string) => {
      setQuery(v);
    },
    [setQuery]
  );

  // 検索クエリがリセットされた時
  const onSuggestionsClearRequested = useCallback(() => {
    setSuggestions([]);
  }, [setSuggestions]);

  // 入力される度にサジェストの中身を更新する
  const onSuggestionsFetchRequested = useCallback(
    (v: string) => {
      const inputValue = v.trim().toLowerCase();
      const inputLength = inputValue.length;

      const filtered =
        inputLength === 0 || !allSuggestions
          ? []
          : allSuggestions.filter(
              (v) => v.toLowerCase().slice(0, inputLength) === inputValue
            );

      setSuggestions(filtered);
    },
    [allSuggestions, setSuggestions]
  );

  // ステータスフィルター
  const onChangeFilterByStatus = useCallback(
    (value) => {
      setFilterByStatus(value);
    },
    [setFilterByStatus]
  );

  const onChangeCurrentDate = useCallback(
    (v: Date) => {
      setCurrentDate(v);
    },
    [setCurrentDate]
  );

  return (
    <DefaultLayout {...props}>
      <SpinnerModal isLoading={isFetching} />
      <Container>
        <button onClick={fetch}>
          <Heading
            tag="h1"
            text={`${formatDate(
              currentDate,
              "YYYY年MM月DD日"
            )}の注文状況を確認する`}
          />
        </button>
        <div className="description">
          <h5 className="name">{breadName}</h5>
          <p className="pickup">
            お渡し日：{formatDate(currentDate, "YYYY年MM月DD日(ddd)")}
          </p>
          <p className="amount">
            <span>現時点受注数：</span>
            <span>
              {totalSoldQuntity}個／{stock}個
            </span>
          </p>
        </div>
        <ReservationOrderDetailFilterForm
          suggestions={suggestions}
          query={query}
          lastPickupDays={lastPickupDays}
          filterByStatus={filterByStatus}
          onChangeQuery={onChangeQuery}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          onSuggestionSelected={onSuggestionSelected}
          onChangeFilterByStatus={onChangeFilterByStatus}
          currentDate={currentDate}
          onChangeCurrentDate={onChangeCurrentDate}
        />
        <div className="orderList">
          <ReservationOrderDetailList
            orders={filteredOrders}
            orderBread={orderBread}
            currentDate={currentDate}
            isFetching={isFetching}
            filterByStatus={filterByStatus}
            filterUsers={filterUsers}
            filterPrevOrders={filterPrevOrders}
            onUpdateIsConfirmed={onUpdateIsConfirmed}
            onUpdateIsCompleted={onUpdateIsCompleted}
          />
        </div>
        <div className="item">
          <PlainHomeButton />
        </div>
      </Container>
    </DefaultLayout>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  padding: ${StyleConfig.HELPER.M20};
  > .lead {
    font-size: 12px;
  }
  > .item {
    margin: ${StyleConfig.HELPER.M24} auto;
  }
  > .orderList {
    margin-top: 20px;
  }
  .description {
    margin-bottom: 28px;
    .name {
      font-size: 20px;
      font-weight: bold;
      margin-bottom: 20px;
      margin-top: 8px;
    }
    .pickup {
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 8px;
      color: ${StyleConfig.COLOR.GREEN};
    }
    .count {
      font-size: 14px;
      span {
        &:first-child {
          color: ${StyleConfig.COLOR.TEXT_GRAY};
        }
        &:last-child {
          font-weight: bold;
        }
      }
    }
  }
`;

const mapStateToProps = (state: State) => {
  const notification = state.notification.reservation.resource;
  const orders = notification.orders.filter((v) => v.status === "succeeded");
  const orderBread = notification.order_breads[0];

  return {
    notification,
    orders,
    orderBread,
    users: state.user.resource,
    usersOrders: state.order.filterByUsers.resource,
    isFetching:
      state.user.isFetching ||
      state.order.isFetching ||
      state.notification.reservation.isFetching,
    error: state.notification.reservation.apiError,
    ordersError: state.notification.reservation.apiError,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getDayReservation: (id: string) =>
    dispatch(NotificationActions.getDayReservation({ id })),
  updateIsConfirmed: (id: string, isConfirmed: boolean) =>
    dispatch(
      OrdersActions.updateOrderIsConfirmed({
        id,
        isConfirmed,
      })
    ),
  updateIsCompleted: (id: string, isCompleted: boolean) =>
    dispatch(
      OrdersActions.updateOrderIsCompleted({
        id,
        isCompleted,
      })
    ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ReservationOrderDetailPage);
